import { useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { getBlogSEO, PUBLISHED_AT, MODIFIED_AT } from "./seo-data";

function upsertMeta(attr: "name" | "property", value: string, content: string) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id: string, data: object) {
  const selector = `script[data-seo-id="${id}"]`;
  let el = document.head.querySelector<HTMLScriptElement>(selector);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function BlogSEO() {
  const { language } = useLanguage();

  useEffect(() => {
    const seo = getBlogSEO(language);

    document.title = seo.title;
    document.documentElement.setAttribute("lang", seo.htmlLang);

    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta("name", "author", seo.author);
    upsertMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1");

    upsertMeta("property", "og:type", "article");
    upsertMeta("property", "og:title", seo.jsonLd.article && (seo.jsonLd.article as { headline: string }).headline);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", seo.url);
    upsertMeta("property", "og:image", seo.ogImage);
    upsertMeta("property", "og:image:alt", (seo.jsonLd.article as { headline: string }).headline);
    upsertMeta("property", "og:locale", seo.ogLocale);
    upsertMeta("property", "og:locale:alternate", seo.ogLocaleAlt);
    upsertMeta("property", "og:site_name", "ReFound");
    upsertMeta("property", "article:published_time", PUBLISHED_AT);
    upsertMeta("property", "article:modified_time", MODIFIED_AT);
    upsertMeta("property", "article:author", seo.author);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", (seo.jsonLd.article as { headline: string }).headline);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", seo.ogImage);

    upsertLink("canonical", seo.url);
    upsertLink("alternate", seo.urlFr, "fr-CA");
    upsertLink("alternate", seo.urlEn, "en-CA");
    upsertLink("alternate", seo.urlFr, "x-default");

    upsertJsonLd("article", seo.jsonLd.article);
    upsertJsonLd("breadcrumb", seo.jsonLd.breadcrumb);
    upsertJsonLd("itemlist", seo.jsonLd.itemlist);
    upsertJsonLd("faq", seo.jsonLd.faq);
  }, [language]);

  return null;
}
